import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Loader2, X, Clock, Plus as PlusIcon, Trash2 } from 'lucide-react'
import { formatDateTime } from '@/lib/utils'
import { usePools } from '@/hooks/use-pools'
import { useIncognito } from '@/hooks/use-incognito'
import { useSystemHistory } from '@/hooks/use-systems'

interface SystemHistoryDialogProps {
  systemID: string
  onClose: () => void
}

export default function SystemHistoryDialog({ systemID, onClose }: SystemHistoryDialogProps) {
  const { data: events = [], isLoading: loading } = useSystemHistory(systemID)
  const { data: pools = [] } = usePools()
  const incognito = useIncognito()

  const getPoolName = (poolID: string) => {
    const pool = pools.find((p) => p.poolID === poolID)
    const name = pool?.name || poolID
    return pool ? incognito.poolName(name) : incognito.uuid(poolID)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>System History</CardTitle>
              <CardDescription className="font-mono text-xs mt-1">
                {incognito.uuid(systemID)}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No history available for this system</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Timeline */}
              <div className="relative border-l-2 border-muted pl-6 space-y-6">
                {events.map((event, index) => (
                  <div key={index} className="relative">
                    {/* Timeline dot */}
                    <div
                      className={`absolute -left-[27px] w-4 h-4 rounded-full border-2 ${
                        event.eventType === 'INSERT'
                          ? 'bg-green-500 border-green-500'
                          : 'bg-red-500 border-red-500'
                      }`}
                    />

                    {/* Event card */}
                    <div className="bg-muted/50 rounded-lg p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {event.eventType === 'INSERT' ? (
                              <PlusIcon className="h-4 w-4 text-green-500" />
                            ) : (
                              <Trash2 className="h-4 w-4 text-red-500" />
                            )}
                            <span className="font-medium">
                              {event.eventType === 'INSERT' ? 'Added to pool' : 'Removed from pool'}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-foreground">
                            {getPoolName(event.poolID)}
                          </p>
                          <p className="text-xs text-muted-foreground font-mono mt-1">
                            {incognito.uuid(event.poolID)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-muted-foreground">
                            {formatDateTime(event.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="mt-6 pt-4 border-t">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Total Events</p>
                    <p className="font-medium text-lg">{events.length}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Pools Visited</p>
                    <p className="font-medium text-lg">
                      {new Set(events.map((e) => e.poolID)).size}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
